/**
 * NotificationService - Frontend Service für Benachrichtigungen
 * Kommuniziert mit der Backend Notifications API
 */

import api from './api';

const NotificationService = {
  /**
   * Holt die Anzahl ungelesener Benachrichtigungen
   * @returns {Promise<number>} Anzahl ungelesener Notifications
   */
  async getUnreadCount() {
    try {
      const response = await api.get('/notifications/unread-count');
      return response.data?.count || 0;
    } catch (error) {
      console.error('Fehler beim Laden der unread count:', error);
      return 0;
    }
  },

  /**
   * Holt alle Benachrichtigungen des Users
   * @param {Object} filter - Filter-Optionen (status, type, limit)
   * @returns {Promise<Array>} Liste der Notifications
   * @throws Bei Netz- oder Serverfehler. Der NotificationContext behält dann
   *   die vorhandene (ggf. gecachte) Liste (Stale-on-Error). Bis Oktober 2026
   *   kam hier [] zurück, und die Liste leerte sich bei jedem Netzfehler.
   */
  async getNotifications(filter = {}) {
    try {
      const response = await api.get('/notifications', { params: filter });
      return response.data?.data || [];
    } catch (error) {
      console.error('Fehler beim Laden der Notifications:', error);
      throw error;
    }
  },

  /**
   * Holt gruppierte ungelesene Counts nach Kategorie
   * @returns {Promise<Object>} { chat, sessions, connections, general, total }
   */
  async getUnreadCounts() {
    try {
      const response = await api.get('/notifications/unread-counts');
      return response.data?.data || {
        chat: 0, sessions: 0, connections: 0, general: 0, total: 0, nonChatTotal: 0,
      };
    } catch (error) {
      console.error('Fehler beim Laden der unread counts:', error);
      return {
        chat: 0, sessions: 0, connections: 0, general: 0, total: 0, nonChatTotal: 0,
      };
    }
  },

  /**
   * Markiert eine Benachrichtigung als gelesen
   * @param {string} notificationId - ID der Notification
   * @returns {Promise<boolean>} Erfolg
   */
  async markAsRead(notificationId) {
    try {
      await api.put(`/notifications/${notificationId}/read`);
      return true;
    } catch (error) {
      console.error('Fehler beim Markieren als gelesen:', error);
      return false;
    }
  },

  /**
   * Markiert alle Benachrichtigungen als gelesen
   * @returns {Promise<boolean>} Erfolg
   */
  async markAllAsRead(options = {}) {
    try {
      await api.put('/notifications/mark-all-read', options);
      return true;
    } catch (error) {
      console.error('Fehler beim Markieren aller als gelesen:', error);
      return false;
    }
  },

  /**
   * Holt die Benachrichtigungs-Einstellungen des Users (Defaults bereits
   * vom Backend dazugemischt: { email, push, inApp }).
   * @returns {Promise<Object|null>} Settings oder null bei Fehler
   */
  async getSettings() {
    try {
      const response = await api.get('/notifications/settings');
      return response.data?.data || null;
    } catch (error) {
      console.error('Fehler beim Laden der Benachrichtigungs-Einstellungen:', error);
      return null;
    }
  },

  /**
   * Speichert Benachrichtigungs-Einstellungen. Das Backend merged in die
   * gespeicherten Werte, es reicht also auch eine einzelne Gruppe.
   * @param {Object} settings - { email?, push?, inApp? } mit Booleans
   * @returns {Promise<Object|null>} Aufgeloeste Gesamt-Settings oder null bei Fehler
   */
  async updateSettings(settings) {
    try {
      const response = await api.put('/notifications/settings', settings);
      return response.data?.success ? (response.data.data || null) : null;
    } catch (error) {
      console.error('Fehler beim Speichern der Benachrichtigungs-Einstellungen:', error);
      return null;
    }
  },
};

export default NotificationService;
